// @greenlight-spec: 59-platform-agent.md
// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { Play } from 'lucide-react';
import { toast } from 'sonner';

import { usePageTitle } from '@/components/app-shell/AppShell';
import { LiveStepLine, useCycle } from '@/components/agents/Transcript';
import { CardGrid } from '@/components/greenlight/CardGrid';
import { EmptyState } from '@/components/greenlight/EmptyState';
import { PageHeader } from '@/components/greenlight/PageHeader';
import { SectionCard } from '@/components/greenlight/SectionCard';
import { SegmentedTabs } from '@/components/greenlight/SegmentedTabs';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { Button } from '@/components/ui/button';
import { useCurrentUser } from '@/lib/auth';
import { isPreviewFeatureEnabled } from '@/lib/preview-features';

import {
  ACTION_KINDS,
  ACTION_REQUESTS,
  AGENTS,
  INFERENCE_SOURCES,
  SESSIONS,
  type ActionMode,
  type ActionRequest,
  type SessionDef,
} from './fixtures';
import { AdapterDialog } from './AdapterDialog';
import { AgentCard } from './AgentCard';
import { LiveActivity } from './LiveActivity';
import {
  ActionRequestCard,
  Ago,
  AgentTile,
  RoadmapTag,
  SessionStatusBadge,
  TriggerChip,
  agentById,
  usd,
} from './shared';
import { StartSessionDialog } from './StartSessionDialog';

const TABS = [
  { key: 'catalog', label: 'Catalog' },
  { key: 'activity', label: 'Activity' },
  { key: 'configuration', label: 'Configuration' },
] as const;
type TabKey = (typeof TABS)[number]['key'];
const isTabKey = (v: string | null): v is TabKey => TABS.some((t) => t.key === v);

export function AgentsPage(): React.JSX.Element {
  usePageTitle('Greenlight Agents');
  const user = useCurrentUser();
  const [params, setParams] = useSearchParams();
  const tab: TabKey = isTabKey(params.get('tab')) ? (params.get('tab') as TabKey) : 'catalog';
  const [starting, setStarting] = React.useState(false);
  const setTab = (v: string) => {
    const next = new URLSearchParams(params);
    if (v === 'catalog') next.delete('tab');
    else next.set('tab', v);
    setParams(next, { replace: true });
  };

  if (!isPreviewFeatureEnabled('greenlight-agents')) return <Navigate to="/apps" replace />;

  const pendingCount = ACTION_REQUESTS.filter((r) => r.status === 'pending').length;

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Platform"
        title="Greenlight Agents"
        actions={
          <Button onClick={() => setStarting(true)}>
            <Play aria-hidden /> Start a session
          </Button>
        }
      />
      {tab !== 'configuration' && <LiveActivity onWaitingClick={() => setTab('activity')} />}
      <SegmentedTabs
        items={TABS.map((t) => ({
          value: t.key,
          label:
            t.key === 'activity' && pendingCount > 0 ? (
              <span className="inline-flex items-center gap-1.5">
                {t.label}
                <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[hsl(var(--status-amber)/0.18)] px-1 font-mono text-[10.5px] text-[hsl(var(--status-amber-text))]">
                  {pendingCount}
                </span>
              </span>
            ) : (
              t.label
            ),
          testId: `agents-tab-${t.key}`,
        }))}
        value={tab}
        onValueChange={setTab}
        aria-label="Greenlight Agents views"
        className="w-full sm:w-auto"
      />
      {tab === 'catalog' && <CatalogTab />}
      {tab === 'activity' && <ActivityTab />}
      {tab === 'configuration' && <ConfigurationTab orgAdmin={user?.role === 'org_admin'} />}
      <StartSessionDialog open={starting} onOpenChange={setStarting} />
    </div>
  );
}

function CatalogTab() {
  const [enabled, setEnabled] = React.useState<Record<string, boolean>>(
    Object.fromEntries(AGENTS.map((a) => [a.id, a.enabled])),
  );
  const available = AGENTS.filter((a) => a.status === 'available');
  const roadmap = AGENTS.filter((a) => a.status === 'roadmap');
  return (
    <div className="space-y-6">
      <CardGrid columns={3} className="md:grid-cols-2 xl:grid-cols-3">
        {available.map((a) => (
          <AgentCard
            key={a.id}
            agent={a}
            enabled={enabled[a.id] ?? false}
            onEnabledChange={(v) => {
              setEnabled((s) => ({ ...s, [a.id]: v }));
              toast.success(`${a.name} ${v ? 'enabled' : 'disabled'}`);
            }}
          />
        ))}
      </CardGrid>
      {roadmap.length > 0 && (
        <section aria-labelledby="coming-later" className="space-y-2">
          <h2 id="coming-later" className="font-mono text-[10.5px] uppercase tracking-[0.06em]">
            Coming later
          </h2>
          <ul className="divide-y divide-border rounded-gl-lg border border-border">
            {roadmap.map((a) => (
              <li key={a.id}>
                <Link
                  to={`/agents/${a.id}`}
                  className="flex min-w-0 items-center gap-3 px-4 py-2.5 hover:bg-accent/40"
                >
                  <AgentTile agentId={a.id} size="sm" muted />
                  <span className="w-44 shrink-0 truncate text-[13px] font-medium">{a.name}</span>
                  <span className="min-w-0 flex-1 truncate text-[12.5px] text-muted-foreground">
                    {a.tagline}
                  </span>
                  <RoadmapTag />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function SessionLine({ session }: { session: SessionDef }) {
  const step = useCycle(session.liveSteps ?? [], 3000);
  if (session.status === 'running' && step) return <LiveStepLine text={step} />;
  return (
    <span className="block truncate text-[12.5px] text-muted-foreground">{session.summary}</span>
  );
}

function ActivityTab() {
  const [requests, setRequests] = React.useState<ActionRequest[]>([...ACTION_REQUESTS]);
  const [filter, setFilter] = React.useState<'waiting' | 'running' | 'all'>('all');
  const pending = requests.filter((r) => r.status === 'pending');
  const sessions = [...SESSIONS].sort((a, b) => b.startedAt.localeCompare(a.startedAt));
  const decide = (id: string, decision: 'approved' | 'denied') => {
    setRequests((rs) => rs.map((r) => (r.id === id ? { ...r, status: decision } : r)));
    toast.success(
      decision === 'approved'
        ? 'Approved — running the stored action'
        : 'Denied — the session has been told',
    );
  };
  return (
    <div className="space-y-6">
      <SectionCard
        title="Waiting on you"
        headerBar
        badge={
          pending.length > 1 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                pending
                  .filter((r) => r.kind === 'merge_pr')
                  .forEach((r) => decide(r.id, 'approved'))
              }
            >
              Approve all {pending.filter((r) => r.kind === 'merge_pr').length} green merges
            </Button>
          )
        }
      >
        <div className="space-y-3 p-4">
          {pending.length === 0 && <EmptyState title="Nothing waiting" />}
          {pending.map((r) => (
            <ActionRequestCard key={r.id} request={r} onDecide={(d) => decide(r.id, d)} />
          ))}
        </div>
      </SectionCard>

      <SectionCard
        title="All sessions"
        headerBar
        badge={
          <SegmentedTabs
            size="sm"
            items={[
              { value: 'waiting', label: 'Needs attention' },
              { value: 'running', label: 'Running' },
              { value: 'all', label: 'Everything' },
            ]}
            value={filter}
            onValueChange={(v) => setFilter(v as 'waiting' | 'running' | 'all')}
            aria-label="Session filter"
          />
        }
      >
        <ul className="divide-y divide-border">
          {sessions
            .filter((s) =>
              filter === 'all'
                ? true
                : filter === 'running'
                  ? s.status === 'running'
                  : s.status === 'waiting' || s.status === 'failed',
            )
            .map((s) => {
              const agent = agentById(s.agentId);
              return (
                <li key={s.id}>
                  <Link
                    to={`/agents/${s.agentId}/sessions/${s.id}`}
                    className="grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-1 px-4 py-3 hover:bg-accent/40 sm:grid-cols-[minmax(0,240px)_1fr_auto_auto]"
                  >
                    <span className="flex min-w-0 items-center gap-2 text-[13px] font-medium">
                      <AgentTile agentId={s.agentId} size="sm" />
                      <span className="truncate">{agent?.name}</span>
                      {agent?.status === 'roadmap' && <RoadmapTag />}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-[13.5px]">{s.title}</span>
                      <SessionLine session={s} />
                    </span>
                    <span className="hidden sm:block">
                      <TriggerChip kind={s.trigger.kind} by={s.trigger.by} />
                    </span>
                    <span className="flex items-center gap-3 justify-self-end text-[12.5px] text-muted-foreground">
                      <SessionStatusBadge status={s.status} />
                      <Ago iso={s.startedAt} />
                    </span>
                  </Link>
                </li>
              );
            })}
        </ul>
      </SectionCard>
    </div>
  );
}

function ConfigurationTab({ orgAdmin }: { orgAdmin: boolean }) {
  const [modes, setModes] = React.useState<Record<string, ActionMode>>(
    Object.fromEntries(ACTION_KINDS.map((k) => [k.id, k.defaultMode])),
  );
  const [expiry, setExpiry] = React.useState(7);
  const [adapterOpen, setAdapterOpen] = React.useState(false);
  return (
    <div className="space-y-6">
      {!orgAdmin && <p className="text-[13px] text-muted-foreground">Read-only</p>}
      <SectionCard title="Module" headerBar>
        <div className="divide-y divide-border">
          <Row label="Greenlight Agents">
            <span className="flex items-center gap-2 text-[13px]">
              <StatusBadge variant="active">Enabled</StatusBadge>
              <span className="text-muted-foreground">
                released to this install by Shift on Oct 14
              </span>
            </span>
          </Row>
          <Row label="Runtime">
            <span className="font-mono text-[12.5px]">
              greenlight-agent 1.42.0 · 1 pod · 2 of 4 slots busy
            </span>
          </Row>
          <Row label="Harness adapter">
            <span className="flex flex-wrap items-center gap-3">
              <span className="font-mono text-[12.5px]">Claude Agent SDK · adapter 1.42.0</span>
              {orgAdmin && (
                <Button size="sm" variant="outline" onClick={() => setAdapterOpen(true)}>
                  Change…
                </Button>
              )}
            </span>
            <AdapterDialog open={adapterOpen} onOpenChange={setAdapterOpen} currentId="claude" />
          </Row>
        </div>
      </SectionCard>

      <SectionCard
        title="Inference sources"
        headerBar
        badge={
          <Button size="sm" variant="outline">
            Add a source
          </Button>
        }
      >
        <ul className="divide-y divide-border">
          {INFERENCE_SOURCES.map((s) => (
            <li key={s.id} className="grid gap-x-4 gap-y-1 px-5 py-3 sm:grid-cols-[1fr_auto]">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[13.5px] font-medium">{s.label}</span>
                  <span className="rounded-md bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                    {s.kind === 'vendor'
                      ? 'Vendor-hosted'
                      : s.kind === 'customer_cloud'
                        ? 'Your cloud'
                        : 'Greenlight-owned'}
                  </span>
                </div>
                <div className="text-[12.5px] text-muted-foreground">
                  {s.identity} · {s.models}
                </div>
              </div>
              <div className="self-center">
                <StatusBadge
                  variant={
                    s.status === 'ready'
                      ? 'active'
                      : s.status === 'pending'
                        ? 'pending'
                        : 'inactive'
                  }
                >
                  {s.status === 'ready'
                    ? 'Ready'
                    : s.status === 'pending'
                      ? 'Setting up'
                      : 'Not set up'}
                </StatusBadge>
              </div>
            </li>
          ))}
        </ul>
      </SectionCard>

      <SectionCard title="Gated actions" headerBar>
        <div className="divide-y divide-border">
          {ACTION_KINDS.map((k) => (
            <Row key={k.id} label={k.label}>
              <SegmentedTabs
                size="sm"
                items={[
                  { value: 'auto', label: 'Automatic' },
                  { value: 'manual', label: 'Needs approval' },
                ]}
                value={modes[k.id] ?? k.defaultMode}
                onValueChange={(v) => setModes((m) => ({ ...m, [k.id]: v as ActionMode }))}
                aria-label={`${k.label} mode`}
              />
            </Row>
          ))}
          <Row label="Approval expiry">
            <span className="flex items-center gap-2 text-[13px]">
              <input
                type="number"
                min={1}
                max={30}
                value={expiry}
                onChange={(e) => setExpiry(Number(e.target.value))}
                className="h-8 w-16 rounded-md border border-input bg-background px-2 text-[13px]"
                aria-label="Approval expiry in days"
              />
              days
            </span>
          </Row>
        </div>
      </SectionCard>

      <SectionCard title="Spend, last 7 days" headerBar>
        <div className="grid grid-cols-2 gap-px bg-border sm:grid-cols-4">
          {[
            ['Sessions', String(SESSIONS.length + 230)],
            ['Tokens', '41.8M'],
            ['Spend', usd(AGENTS.reduce((n, a) => n + a.health.spend7d, 0))] as [string, string],
            ['Failed', String(AGENTS.reduce((n, a) => n + a.health.failed7d, 0))],
          ].map(([l, v]) => (
            <div key={l} className="bg-card px-5 py-4">
              <div className="text-[12px] text-muted-foreground">{l}</div>
              <div className="font-mono text-[18px]">{v}</div>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-x-6 gap-y-2 px-5 py-3 sm:grid-cols-[260px_1fr] sm:items-center">
      <div>
        <div className="text-[13.5px] font-medium">{label}</div>
      </div>
      <div>{children}</div>
    </div>
  );
}
